import { ClientDataType } from "@/api/ClientApi";
import { ZoneDataType } from "@/api/ZoneApi";
import { normalizeIdentification } from "@utils/Identification";
import { ColumnMapping, mapRowToClient } from "./clientImportMapping";
import { collectMissingLookupNames, nextAvailableCode as nextCodeFrom, resolveRows } from "./core/resolveRows";
import { DuplicatePolicy, ResolveResult as GenericResolveResult, ResolvedRow as GenericResolvedRow, RowStatus } from "./core/types";

export type { DuplicatePolicy, RowStatus };

export type ResolvedRow = GenericResolvedRow<ClientDataType>;

export type ResolveResult = GenericResolveResult<ClientDataType> & {
    /** Zonas del archivo que todavia no existen en el tenant. */
    missingZoneNames: string[];
};

export const normalizeZoneName = (raw?: string | null): string => `${raw ?? ""}`.trim().toUpperCase();

/** Indice de zonas existentes por nombre normalizado. */
export const buildZoneIndex = (zones: ZoneDataType[]): Map<string, string> => {
    const index = new Map<string, string>();
    zones.forEach((zone) => {
        const key = normalizeZoneName(zone.nombre);
        if (key && zone.id && !index.has(key)) index.set(key, zone.id);
    });
    return index;
};

/** Siguiente codigo consecutivo disponible entre los registros ya existentes. */
export const nextAvailableCode = (items: Array<{ codigo?: unknown }>): number =>
    nextCodeFrom(items.map((item) => item.codigo as string | number | null | undefined));

/**
 * Transforma las filas del archivo en clientes listos para importar: normaliza los
 * valores, resuelve las zonas, detecta duplicados contra lo que ya existe en el tenant
 * y asigna los codigos que falten.
 */
export const resolveImportRows = (params: {
    dataRows: string[][];
    mapping: ColumnMapping;
    existingClients: ClientDataType[];
    existingZones: ZoneDataType[];
    policy: DuplicatePolicy;
}): ResolveResult => {
    const { dataRows, mapping, existingClients, existingZones, policy } = params;

    const zoneIndex = buildZoneIndex(existingZones);

    const { rows } = resolveRows<ClientDataType, ClientDataType>({
        dataRows,
        mapping,
        policy,
        existing: existingClients,
        entitySingular: "cliente",

        mapRow: (row, columnMapping) => {
            const { client, zoneName } = mapRowToClient(row, columnMapping);
            return { data: client, lookupName: zoneName };
        },

        prepare: (client, zoneName) => {
            const messages: string[] = [];

            if (zoneName) {
                const zoneId = zoneIndex.get(normalizeZoneName(zoneName));
                if (zoneId) client.zoneId = zoneId;
            }

            if (!`${client.nombre ?? ""}`.trim()) {
                return { error: "La fila no tiene nombre de cliente", messages };
            }

            if (!`${client.identificacion ?? ""}`.trim()) {
                messages.push("Sin identificacion: no podra usarse en factura electronica");
            }

            return { error: null, messages };
        },

        duplicateKeys: (client) => [
            codeKey(client.codigo),
            identificationKey(client.identificacion),
        ],
        existingKeys: (client) => [codeKey(client.codigo), identificationKey(client.identificacion)],
        idOf: (client) => client.id,

        codeOf: (client) => `${client.codigo ?? ""}`,
        setCode: (client, code) => {
            client.codigo = code;
        },
        existingCodeOf: (client) => `${client.codigo ?? ""}`,
    });

    const missingZoneNames = collectMissingLookupNames(rows, (row) => !!row.data.zoneId);

    return { rows, missingZoneNames };
};

const codeKey = (codigo?: unknown): string => {
    const value = `${codigo ?? ""}`.trim();
    return value ? `codigo:${value}` : "";
};

const identificationKey = (identificacion?: unknown): string => {
    const value = normalizeIdentification(`${identificacion ?? ""}`);
    return value ? `identificacion:${value}` : "";
};

/** Rellena `zoneId` en las filas cuya zona se acaba de crear. */
export const applyCreatedZones = (rows: ResolvedRow[], zoneIndex: Map<string, string>): ResolvedRow[] =>
    rows.map((row) => {
        if (!row.lookupName || row.data.zoneId) return row;

        const zoneId = zoneIndex.get(normalizeZoneName(row.lookupName));
        return zoneId ? { ...row, data: { ...row.data, zoneId } } : row;
    });
